import { request } from './client'
import type { Skill } from '../types/prisma-enums'
import type { StudentRow } from './classes'

/**
 * The six teacher reads, one typed function each.
 *
 * 🔴 EVERY ONE SENDS `context: 'user'` AND WILL 401 TODAY. Staff login is
 * Unit 9. That is the point: D1–D7 call these, so the screens are wired to real
 * paths rather than to a fixture, and the missing piece is a credential.
 */

export interface RosterRow {
  playerId: string
  rollNumber: string | null
  name: string
  parentName: string | null
  /** 🔴 Already masked by the server — the full number never reaches us. */
  parentPhone: string | null
  lastPlayed: string | null
}

export type AttentionReason = 'IDLE' | 'LOW_ACCURACY'

export interface AttentionRow {
  playerId: string
  name: string
  rollNumber: string | null
  reasons: AttentionReason[]
  daysIdle: number | null
  accuracy: number | null
}

/** The two counts an accuracy is made of. 🔴 Never the ratio — the screen divides. */
export interface Answers {
  correct: number
  attempts: number
}

/**
 * One seven-day window of a class, in the shape both windows share.
 *
 * 🔴 `thisWeek` AND `lastWeek` ARE THE SAME TYPE ON PURPOSE. A tile that draws a
 * change draws it by subtracting two values of this interface, so there is no
 * per-metric "and the previous one is called…" to get wrong.
 */
export interface ClassWeek {
  /** Children with at least one sitting in the window. */
  activeChildren: number
  /** Sittings that started in the window — NOT the same number as above. */
  sessions: number
  /** Mean whole minutes per finished sitting; null when none finished. */
  avgSessionMinutes: number | null
  /**
   * 🔴 THE MEAN'S DENOMINATOR (OD-39). A tablet closed mid-round writes no end
   * time, so this is routinely lower than `sessions` — and a mean drawn from one
   * finished sitting must not be printed as though it described all of them.
   */
  timedSessions: number
  answers: Answers
}

export interface ClassOverview {
  classId: string
  className: string
  childCount: number
  activeThisWeek: number
  sessionsThisWeek: number
  /** This week in full — including the two measures the counts above cannot carry. */
  thisWeek: ClassWeek
  /** The seven days before it, so a tile can show which way a number is moving. */
  lastWeek: ClassWeek
  topOfClass: { playerId: string; name: string; coins: number } | null
  attention: {
    needsAttention: AttentionRow[]
    /** Never played — a different fact from "idle", and a different action. */
    notStarted: { playerId: string; name: string; rollNumber: string | null }[]
  }
}

export interface LeaderboardRow {
  playerId: string
  rollNumber: string | null
  name: string
  coins: number
  stickers: number
  streakDays: number
  /** ISO instant of the child's last round, or null when they have never played. */
  lastPlayed: string | null
  topSkill: string | null
}

export interface EventRow {
  id: string
  game: string
  type: string
  skill: string | null
  level: number | null
  createdAt: string
}

export interface Insight {
  summary: string
  strengths: string[]
  focusAreas: string[]
  generatedBy: string
}

export interface NcfCoverage {
  rows: { game: string; category: string; codes: string[]; events: number; children: number }[]
  codesCovered: string[]
  codesUncovered: string[]
}

/** The staff access token. Supplied by the caller; this module stores nothing. */
export interface StaffAuth {
  token?: string
  signal?: AbortSignal
}

const get = <T>(path: string, auth: StaffAuth): Promise<T> =>
  request<T>(path, { context: 'user', token: auth.token, signal: auth.signal })

/**
 * `GET /students?classId=` — one class's children (P1-62). The unfiltered
 * `getAllStudents` (classes.ts) is every class the caller can see, which for a
 * coordinator over many sections is unbounded; a class screen asks for one.
 */
export const getClassStudents = (classId: string, auth: StaffAuth = {}): Promise<StudentRow[]> =>
  get(`/students?classId=${encodeURIComponent(classId)}`, auth)

export const getRoster = (classId: string, auth: StaffAuth = {}): Promise<RosterRow[]> =>
  get(`/classes/${encodeURIComponent(classId)}/roster`, auth)

export const getClassOverview = (classId: string, auth: StaffAuth = {}): Promise<ClassOverview> =>
  get(`/classes/${encodeURIComponent(classId)}/overview`, auth)

/** One day of one class's play. `day` is `YYYY-MM-DD`, UTC. */
export interface ActivityPoint {
  day: string
  /** Sittings that started on this day. */
  sessions: number
  /** Distinct children who had at least one sitting — NOT the same as `sessions`. */
  children: number
}

export interface ClassActivity {
  classId: string
  generatedAt: string
  windowDays: number
  /** 🔴 What the numbers mean, shipped with them — OD-39/OD-72 applied to time. */
  basis: string
  points: ActivityPoint[]
}

/**
 * This class over time — the series D4's tiles cannot carry.
 *
 * 🔴 `overview` ANSWERS *THIS WEEK* IN THREE SCALARS, and three scalars cannot
 * tell a class that never started from one that stopped a fortnight ago. Nothing
 * else in the teaching API carried a date series at all.
 *
 * ⚠️ EVERY DAY IN THE WINDOW COMES BACK, INCLUDING THE ZEROES. A caller must not
 * filter them out to "tidy" the chart: closing the gaps draws four sittings in a
 * fortnight as four consecutive days.
 *
 * ⚠️ `days` IS CLAMPED SERVER-SIDE TO 7–90, and anything outside becomes 30
 * rather than a 400 — a mistyped number should draw a chart, not an error.
 */
export const getClassActivity = (
  classId: string,
  days: number,
  auth: StaffAuth = {},
): Promise<ClassActivity> =>
  get(
    `/classes/${encodeURIComponent(classId)}/activity?days=${encodeURIComponent(String(days))}`,
    auth,
  )

/* ──────────────────────────────────────────── what the class is learning ── */

export interface ClassSkillRow {
  skill: Skill
  /** Skill-bearing events across the class. */
  events: number
  /** Children with at least one event in this skill — the honest denominator. */
  children: number
}

/**
 * Which of the five skills this class has actually touched.
 *
 * 🔴 THE ONE READ ON D4 THAT IS ABOUT *WHAT*, NOT *HOW MUCH*. Every tile on that
 * screen counts activity, and a class can lead all six while never once opening
 * anything but Sky Quest. The games panel answers that per GAME; this answers it
 * per SKILL, which is the axis a week is planned on.
 *
 * ⚠️ EVERY LIVE SKILL COMES BACK, INCLUDING THE UNTOUCHED ONES. *"Nothing has
 * happened in Matching & Sorting"* is a real answer, and a list that quietly got
 * shorter would read as though the curriculum had.
 */
export interface ClassSkills {
  classId: string
  className: string
  /** The register — the denominator for every row's `children`. */
  childCount: number
  rows: ClassSkillRow[]
  /** Children with any skill-bearing event at all. */
  childrenWithAnyEvents: number
  totalEvents: number
}

/**
 * ⚠️ ITS OWN REQUEST, NOT A FIELD ON `overview`. It reads every event the class
 * has ever produced and folds it five ways — the heaviest read D4 makes — and
 * the row of tiles a teacher opens the screen for must not wait on it.
 */
export const getClassSkills = (classId: string, auth: StaffAuth = {}): Promise<ClassSkills> =>
  get(`/classes/${encodeURIComponent(classId)}/skills`, auth)

export const getLeaderboard = (classId: string, auth: StaffAuth = {}): Promise<LeaderboardRow[]> =>
  get(`/leaderboard?classId=${encodeURIComponent(classId)}`, auth)

export const getPlayerEvents = (playerId: string, auth: StaffAuth = {}): Promise<EventRow[]> =>
  get(`/players/${encodeURIComponent(playerId)}/events`, auth)

export const getInsight = (playerId: string, auth: StaffAuth = {}): Promise<Insight> =>
  get(`/ai/insights/${encodeURIComponent(playerId)}`, auth)

export const getCoverage = (classId: string, auth: StaffAuth = {}): Promise<NcfCoverage> =>
  get(`/classes/${encodeURIComponent(classId)}/coverage`, auth)

/**
 * D7's spreadsheet. A path rather than a fetch: the browser downloads it with
 * the same credential the page holds, and streaming a file through JS only to
 * hand it back to the browser buys nothing.
 *
 * ⚠️ Which also means it is the one call that cannot send an Authorization
 * header — a plain link carries cookies, and this API uses bearer tokens. So
 * D7 fetches it like the others when the token exists; the path is exported for
 * the day a signed download URL is worth building.
 */
export const coverageCsvPath = (classId: string): string =>
  `/classes/${encodeURIComponent(classId)}/coverage.csv`

/**
 * D7's spreadsheet, fetched.
 *
 * 🔴 IT FETCHES AND BUILDS THE BLOB RATHER THAN LINKING TO THE PATH ABOVE, for
 * the reason that path records: a plain `<a href>` carries cookies and this API
 * uses bearer tokens, so the link downloads a 401 page named `ncf-coverage.csv`.
 * That is why D7's button did nothing at all until now — it resolved the path
 * and dropped it, because a download that saves an error page is worse than a
 * button that admits it cannot.
 *
 * ⚠️ THE SERVER'S FILE, NOT ONE BUILT HERE. `schoolDashboardModel.ts` builds E5's
 * copy in the browser and calls itself *"a convenience, not the record"* — it
 * predates `expect: 'text'`, which `client.ts` added for X9's audit export. The
 * escaping against spreadsheet formula injection lives on the server
 * (`csv-cell.ts`), and this route is the one place D7's numbers should come
 * from. A fifth copy of that escaper is a fifth place to get it wrong.
 */
export const coverageCsv = (classId: string, auth: StaffAuth = {}): Promise<string> =>
  request<string>(coverageCsvPath(classId), {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
    // The body is `text/csv`. Parsed as JSON it would throw and be classified
    // retryable — a download that succeeded, retried forever. See `client.ts`.
    expect: 'text',
  })

/* ─────────────────────────────────────────────── strengths and gaps ── */

/**
 * Where one child is strong and where they are stuck.
 *
 * 🔴 IT REPLACED A NUMBER THAT MEANT SOMETHING ELSE. `GET /ai/insights/:id`
 * ranks a child's skills by EVENT COUNT and reports the top two as
 * *"strengths"* — so a child who spent the week failing Letter Lagoon was
 * reported as strong at literacy. Activity is not attainment. This reads the
 * answers themselves, against the child's own classmates.
 *
 * ⚠️ THERE IS NO PARENT-FACING VERSION OF THIS READ, DELIBERATELY. The verdict
 * is built by comparing a child with other people's children, and the parent
 * tier refuses comparisons in as many words. The server's role list enforces it.
 */
export type Standing = 'STRONG' | 'STEADY' | 'NEEDS_PRACTICE' | 'NOT_ENOUGH_YET' | 'NOT_STARTED'

export interface SkillStanding {
  skill: Skill
  standing: Standing
  /** 🔴 The numerator and the denominator, always — never the ratio alone. */
  correct: number
  attempts: number
  /** 0–1, or null below the attempts floor. The screen prints the fraction. */
  accuracy: number | null
  /** The same for their classmates, excluding this child. Null when too few. */
  classAccuracy: number | null
  classAttempts: number
  topLevel: number | null
  mastered: number
  /**
   * 🔴 LEVELS THEY WERE MOVED UP FROM WITHOUT MASTERING. `play.prisma`: *"Ceiling
   * advancement LOOKS IDENTICAL TO SUCCESS in every accuracy average, so the
   * ceiling count is the only signal that a level's content is too hard."* It is
   * separate from `standing` because a child can be STEADY and still be here —
   * and that combination is the most useful thing on the screen.
   */
  ceiling: number
}

export interface StrengthsReport {
  skills: SkillStanding[]
  /** How many classmates the comparison was drawn from. Zero is a real answer. */
  peers: number
}

export const getStrengths = (playerId: string, auth: StaffAuth = {}): Promise<StrengthsReport> =>
  get(`/players/${encodeURIComponent(playerId)}/strengths`, auth)
